import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { firstValueFrom, forkJoin } from 'rxjs';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmSkeleton } from '@lite-workspace/shared/helm-ui/skeleton';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { AvatarComponent } from '@lite-workspace/shared/blocks/avatar';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { WizardHelpButtonComponent } from '@lite-workspace/shared/blocks/wizard-shell';
import { pluralRu } from '@lite-workspace/shared/format';
import { isFolderKind, itemNameOf } from '@lite-workspace/dto-design-system';
import { projectPrintItems } from '@lite-workspace/dto-fotoclass-book';
import { TelegramContactService } from '../../../ui/telegram-contact';
import { BillingApiService } from '../../account/domain/infrastructure/billing-api.service';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { PrintCatalogFacade } from '../domain/application/print-catalog.facade';
import type { PrintProductEntry } from '../domain/infrastructure/print-services-api.service';
import { isBalanceShortfall } from '../domain/utils/export-error.utils';
import { foldersFirst, itemPluralName } from './print-files-dialog.component';

export interface BulkDownloadAlbum {
  id: string;
  name: string;
  avatar: string | null;
  approved: boolean;
  personId?: string | null;
  /** The album's own print product; absent = the project's. */
  printProductId?: string | null;
}

/** Кого просят оплатить: весь файл печати проекта («Скачать альбомы») или макет одного альбома
 *  (пункт меню карточки). Списание в обоих случаях ОДНО — проектное и разовое; отличаются только
 *  заголовок и строка подтверждения, поэтому шлюз один на оба входа. */
export type PrintPaymentScope = 'project' | 'album';

export interface BulkDownloadAlbumsDialogContext {
  projectId: string;
  /** A promise lets the dialog open on the click itself and resolve the list behind its loading
   *  skeleton; `null` means the opener took over (gate refused) and is closing this dialog. */
  albums: BulkDownloadAlbum[] | Promise<BulkDownloadAlbum[] | null>;
  scope?: PrintPaymentScope;
}

const UNAPPROVED_PREVIEW_COUNT = 4;
const FOLDER_WAITING: [string, string, string] = ['папка ждёт', 'папки ждут', 'папок ждут'];
const ALBUM_WAITING: [string, string, string] = ['альбом ждёт', 'альбома ждут', 'альбомов ждут'];

type Phase = 'loading' | 'blocked' | 'pay' | 'charging' | 'success' | 'unlocked';

/** One print item of the project: its albums and how many of them still wait for approval. */
interface ItemRow {
  key: string;
  name: string;
  total: number;
  approvedIds: string[];
  /** Tooltip of the locked «Скачать»; `null` = every album of the item is approved. */
  waiting: string | null;
}

@Component({
  selector: 'app-bulk-download-albums-dialog',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    HlmButton,
    HlmSkeleton,
    HlmTooltip,
    AvatarComponent,
    IconComponent,
    DialogSheetComponent,
    WizardHelpButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass' },
  // Height hugs the phase on screen: no footprint min-height, or the short paid/success phases trail
  // an empty band under their buttons. The skeleton mirrors the paid phase — the one every repeat
  // download opens on.
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 class="mb-5 flex items-center gap-2.5 text-lg font-bold text-ink">
          {{ heading }}
          <app-wizard-help-button helpId="albums-download" />
        </h2>

        <ng-template #counters>
          <div class="mb-5 grid grid-cols-3 gap-2">
            <div class="rounded-[var(--r-l)] border border-line2 bg-bg2 px-4 py-3">
              <div class="text-2xl font-semibold text-ink">{{ total() }}</div>
              <div class="mt-0.5 text-xs text-c3">всего альбомов</div>
            </div>
            <div class="rounded-[var(--r-l)] border border-line2 bg-bg2 px-4 py-3">
              <div class="text-2xl font-semibold text-ok">{{ approvedCount() }}</div>
              <div class="mt-0.5 text-xs text-c3">утверждено</div>
            </div>
            @if (unapproved().length > 0) {
              <div class="rounded-[var(--r-l)] border border-warn-line bg-warn-soft px-4 py-3">
                <div class="text-2xl font-semibold text-warn">{{ unapproved().length }}</div>
                <div class="mt-0.5 text-xs text-c3">осталось утвердить</div>
              </div>
            } @else {
              <div class="rounded-[var(--r-l)] border border-line2 bg-bg2 px-4 py-3">
                <div class="text-2xl font-semibold text-ink">0</div>
                <div class="mt-0.5 text-xs text-c3">осталось утвердить</div>
              </div>
            }
          </div>
        </ng-template>

        <ng-template #paidNote>
          <div class="flex items-start gap-3 rounded-[var(--r-l)] border border-ok-line bg-ok-soft px-4 py-3">
            <span class="mt-0.5 inline-flex shrink-0"><app-icon name="unlock" [size]="18" tone="ok" /></span>
            <p class="text-sm leading-snug text-ink">Файл печати уже оплачен — скачивание доступно без ограничений.</p>
          </div>
        </ng-template>

        <ng-template #payment>
          <div class="mb-4 rounded-[var(--r-l)] border border-line2 bg-bg2 px-5 py-4">
            <div class="flex items-baseline justify-between">
              <span class="text-sm text-c2">Файл печати проекта</span>
              <span class="text-2xl font-semibold text-ink">{{ formatRub(price()) }} ₽</span>
            </div>
            <p class="mt-1.5 text-xs leading-snug text-c3">
              Цена одна на весь проект — количество альбомов на неё не влияет. Оплата разовая: после неё файл печати можно
              скачивать сколько угодно раз.
            </p>
          </div>

          <div class="flex items-center gap-2.5 px-1">
            <app-icon name="wallet" [size]="16" tone="c3" />
            <span class="text-sm text-c2">На балансе: {{ formatRub(balance()) }} ₽</span>
          </div>

          @if (insufficient()) {
            <div class="mt-5 rounded-[var(--r-l)] border border-warn-line bg-warn-soft px-4 py-3">
              <p class="text-sm leading-snug text-ink">Не хватает {{ formatRub(shortfall()) }} ₽.</p>
              <p class="mt-1 text-xs leading-snug text-c3">
                Пополнить баланс можно через поддержку — напишите нам, и мы поможем.
              </p>
              <button hlmBtn type="button" variant="outline" size="sm" class="mt-2.5" (click)="openSupport()">
                Написать в поддержку
              </button>
            </div>
          }

          <div class="mt-6 flex justify-end gap-2">
            <button hlmBtn type="button" variant="outline" (click)="close()">Отменить</button>
            <button hlmBtn type="button" variant="default" [disabled]="insufficient()" (click)="charge()">
              Оплатить {{ formatRub(price()) }} ₽
            </button>
          </div>
        </ng-template>

        @if (perItem() && phase() !== 'loading' && phase() !== 'charging') {
          <ng-container [ngTemplateOutlet]="counters" />
          @if (phase() === 'unlocked') {
            <div class="mb-5"><ng-container [ngTemplateOutlet]="paidNote" /></div>
          }

          <div class="flex flex-col gap-3">
            @for (row of itemRows(); track row.key) {
              <div class="flex items-center gap-4 rounded-[var(--r-l)] border border-line2 px-5 py-4">
                <div class="min-w-0">
                  <p class="text-base font-semibold text-ink">{{ row.name }}</p>
                  <p class="mt-1 text-sm text-c2">Утверждены {{ row.approvedIds.length }} из {{ row.total }}</p>
                </div>
                @if (phase() === 'unlocked') {
                  <!-- Выключенная кнопка не диспатчит mouse-события: подсказку ловит обёртка. -->
                  <span class="ml-auto inline-flex shrink-0" [hlmTooltip]="row.waiting ?? ''" [tooltipDisabled]="!row.waiting">
                    <button hlmBtn type="button" variant="default" [disabled]="!!row.waiting" (click)="downloadItem(row)">
                      Скачать
                    </button>
                  </span>
                }
              </div>
            }
          </div>

          @if (phase() === 'pay') {
            <div class="mt-5"><ng-container [ngTemplateOutlet]="payment" /></div>
          } @else {
            <div class="mt-6 flex justify-end">
              <button hlmBtn type="button" variant="outline" (click)="close()">Закрыть</button>
            </div>
          }
        } @else {
          @switch (phase()) {
            @case ('loading') {
              <div hlmSkeleton class="h-16 rounded-[var(--r-l)]"></div>
              <div class="mt-6 flex justify-end gap-2">
                <div hlmSkeleton class="h-9 w-24 rounded-[var(--r-s)]"></div>
                <div hlmSkeleton class="h-9 w-24 rounded-[var(--r-s)]"></div>
              </div>
            }

            @case ('blocked') {
              <ng-container [ngTemplateOutlet]="counters" />

              <p class="mb-4 text-sm leading-snug text-c2">
                Файл печати станет доступен, когда будут утверждены все альбомы. Осталось совсем немного — проверьте эти:
              </p>

              <div
                class="rounded-[var(--r-l)] border border-line2"
                [class.max-h-66]="expanded()"
                [class.overflow-y-auto]="expanded()"
              >
                @for (album of visibleUnapproved(); track album.id) {
                  <div class="flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0">
                    <div class="shrink-0"><app-avatar [src]="album.avatar" size="8" ring="none" /></div>
                    <span class="truncate text-sm text-ink">{{ album.name }}</span>
                    <span class="ml-auto shrink-0 text-xs text-warn">не утверждён</span>
                  </div>
                }
              </div>
              @if (hiddenCount() > 0) {
                <button
                  type="button"
                  class="mt-2 text-sm text-acc transition-colors duration-[var(--t-fast)] hover:text-acc2"
                  (click)="expanded.set(true)"
                >
                  Показать ещё {{ hiddenCount() }}
                </button>
              }

              <div class="mt-6 flex justify-end">
                <button hlmBtn type="button" variant="outline" (click)="close()">Закрыть</button>
              </div>
            }

            @case ('pay') {
              <div class="mb-5 flex items-center gap-2.5 rounded-[var(--r-l)] border border-ok-line bg-ok-soft px-4 py-3">
                <app-icon name="check" [size]="16" tone="ok" />
                <span class="text-sm text-ink">{{ approvalLine() }}</span>
              </div>

              <ng-container [ngTemplateOutlet]="payment" />
            }

            @case ('charging') {
              <div class="flex flex-col items-center gap-4 py-8">
                <span
                  class="size-9 animate-spin rounded-full border-2 border-line2 border-t-acc motion-reduce:animate-none"
                  aria-hidden="true"
                ></span>
                <span class="text-sm text-c2">Списываем оплату…</span>
              </div>
            }

            @case ('success') {
              <div class="flex flex-col items-center gap-3 py-3 text-center">
                <span class="grid size-12 place-items-center rounded-full border border-ok-line bg-ok-soft">
                  <app-icon name="check" [size]="22" tone="ok" />
                </span>
                <div>
                  <p class="text-base font-semibold text-ink">Оплачено</p>
                  <p class="mt-1 text-sm text-c2">На балансе осталось {{ formatRub(balance()) }} ₽</p>
                </div>
                <p class="max-w-90 text-xs leading-snug text-c3">
                  Файл печати теперь ваш навсегда — скачивайте его в любой момент, повторная оплата не потребуется.
                </p>
              </div>
              <div class="mt-6 flex justify-end">
                <button hlmBtn type="button" variant="default" (click)="download()">Скачать</button>
              </div>
            }

            @case ('unlocked') {
              <ng-container [ngTemplateOutlet]="paidNote" />
              <div class="mt-6 flex justify-end gap-2">
                <button hlmBtn type="button" variant="outline" (click)="close()">Закрыть</button>
                <button hlmBtn type="button" variant="default" (click)="download()">Скачать</button>
              </div>
            }
          }
        }
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class BulkDownloadAlbumsDialogComponent {
  protected readonly ctx = injectBrnDialogContext<BulkDownloadAlbumsDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);
  private readonly billing = inject(BillingApiService);
  private readonly telegramContact = inject(TelegramContactService);
  private readonly projects = inject(ProjectsFacade);
  private readonly catalog = inject(PrintCatalogFacade);

  protected readonly hostClass = dialogSheetHostClass('project', 'p-9');

  /** Header «Скачать» (no scope) of a project with two items and more gets a row per print item; a one-item project
   *  and the pay gates (`ensurePaid`, scope named) keep the project-wide window with one «Скачать». */
  protected readonly perItem = signal(false);
  private readonly scope: PrintPaymentScope = this.ctx.scope ?? 'project';
  protected readonly heading = this.scope === 'album' ? 'Макет альбома' : 'Файл печати';

  protected readonly phase = signal<Phase>('loading');
  protected readonly expanded = signal(false);
  protected readonly balance = signal(0);
  protected readonly price = signal(0);

  private readonly albums = signal<BulkDownloadAlbum[]>(Array.isArray(this.ctx.albums) ? this.ctx.albums : []);

  protected readonly total = computed(() => this.albums().length);
  protected readonly unapproved = computed(() => this.albums().filter(album => !album.approved));
  protected readonly approvedCount = computed(() => this.total() - this.unapproved().length);
  protected readonly visibleUnapproved = computed(() =>
    this.expanded() ? this.unapproved() : this.unapproved().slice(0, UNAPPROVED_PREVIEW_COUNT),
  );

  protected readonly hiddenCount = computed(() => this.unapproved().length - this.visibleUnapproved().length);
  // Счёт по проекту читателю, пришедшему за одним макетом, ничего не говорит — он про свой альбом.
  protected readonly approvalLine = computed(() =>
    this.scope === 'album' ? 'Альбом утверждён' : `Все альбомы утверждены: ${this.approvedCount()} из ${this.total()}`,
  );

  /** Products of the project's items (main first) and the main id; a failed read leaves the default name. */
  private readonly items = signal<{ products: PrintProductEntry[]; main: string | null }>({ products: [], main: null });

  /** Item rows, folders first, then project item order: group = the album's own product, else the main one; an item
   *  without albums has no row. */
  protected readonly itemRows = computed<ItemRow[]>(() => {
    const { main } = this.items();
    const products = foldersFirst(this.items().products);
    const groups = new Map<string, BulkDownloadAlbum[]>();
    for (const album of this.albums()) {
      const key = album.printProductId ?? main ?? '';
      groups.set(key, [...(groups.get(key) ?? []), album]);
    }

    const order = (key: string): number => {
      const index = products.findIndex(product => product.id === key);
      return index < 0 ? products.length : index;
    };

    return [...groups.entries()]
      .sort(([a], [b]) => order(a) - order(b))
      .map(([key, albums]) => {
        const product = products.find(item => item.id === key);
        const approvedIds = albums.filter(album => album.approved).map(album => album.id);
        const left = albums.length - approvedIds.length;
        const forms = isFolderKind(product?.productKind) ? FOLDER_WAITING : ALBUM_WAITING;
        return {
          key,
          name: itemPluralName(product ? itemNameOf(products, product) : 'Альбом'),
          total: albums.length,
          approvedIds,
          waiting: left > 0 ? `${left} ${pluralRu(left, forms)} утверждения` : null,
        };
      });
  });

  protected readonly insufficient = computed(() => this.balance() < this.price());
  protected readonly shortfall = computed(() => Math.max(0, this.price() - this.balance()));

  constructor() {
    void this.init();
  }

  private async init(): Promise<void> {
    if (!Array.isArray(this.ctx.albums)) {
      const resolved = await this.ctx.albums.catch((): null => null);
      // `null`: the opener's gate refused and is swapping this dialog out — nothing left to render.
      if (resolved === null) return;
      this.albums.set(resolved);
    }

    // The item count decides the window, so the items are read before it opens.
    if (this.ctx.scope === undefined && (await this.loadItems()) > 1) {
      this.perItem.set(true);
      // Per item a folder may go out while albums still wait: only a project with nothing approved stops here.
      if (this.approvedCount() === 0) {
        this.phase.set('blocked');
        return;
      }

      await this.loadBilling();
      return;
    }

    if (this.unapproved().length > 0) {
      this.phase.set('blocked');
      return;
    }

    await this.loadBilling();
  }

  /** Resolves the number of the project's print items (its settings, not the catalog reads that answered). */
  private async loadItems(): Promise<number> {
    const projectId = this.ctx.projectId;
    if (!this.projects.baseSettingsByProject()[projectId]) {
      await this.projects.loadProjectBaseSettings(projectId).catch(() => undefined);
    }

    const settings = this.projects.baseSettingsByProject()[projectId];
    const products = await Promise.all(
      projectPrintItems(settings).map(id => firstValueFrom(this.catalog.getProduct(id)).catch((): null => null)),
    );
    this.items.set({
      products: products.filter((product): product is PrintProductEntry => !!product),
      main: settings?.photoBook?.printProductId || null,
    });
    return projectPrintItems(settings).length;
  }

  private async loadBilling(): Promise<void> {
    try {
      const { status, balance, tariff } = await firstValueFrom(
        forkJoin({
          status: this.billing.getChargeStatus(this.ctx.projectId),
          balance: this.billing.getBalance(),
          tariff: this.billing.getTariff(),
        }),
      );
      this.balance.set(balance.amount);
      this.price.set(tariff.albumPrice);
      this.phase.set(status.charged ? 'unlocked' : 'pay');
    } catch {
      this.dialogRef.close('billing-error');
    }
  }

  protected async charge(): Promise<void> {
    if (this.phase() !== 'pay' || this.insufficient()) return;
    this.phase.set('charging');
    try {
      const balance = await firstValueFrom(this.billing.chargeProject(this.ctx.projectId));
      this.balance.set(balance.amount);
      this.phase.set(this.perItem() ? 'unlocked' : 'success');
    } catch (error) {
      if (isBalanceShortfall(error)) {
        this.phase.set('pay');
        try {
          const fresh = await firstValueFrom(this.billing.getBalance());
          this.balance.set(fresh.amount);
        } catch {
          this.balance.set(0);
        }

        return;
      }

      this.dialogRef.close('billing-error');
    }
  }

  protected download(): void {
    this.dialogRef.close(this.albums().map(album => album.id));
  }

  protected downloadItem(row: ItemRow): void {
    if (row.waiting) return;
    this.dialogRef.close(row.approvedIds);
  }

  protected close(): void {
    this.dialogRef.close(undefined);
  }

  protected openSupport(): void {
    this.dialogRef.close(undefined);
    this.telegramContact.open('topup');
  }

  protected formatRub(value: number): string {
    return new Intl.NumberFormat('ru-RU', { useGrouping: true }).format(value);
  }
}
